import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Project, Category } from '@rappider/rappider-sdk';
import { Store } from '@ngrx/store';
import { selectActiveProject } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';
import { selectPublicProjectsWithCategories, selectModuleOperationsLoading } from 'libs/project/src/lib/states/projects-state/project.selectors';
import * as ProjectActions from 'libs/project/src/lib/states/projects-state/project.actions';
import { Subscription } from 'rxjs';
import { ListMode } from '@rappider/rappider-components';
import { PreviewGridType } from 'libs/component-definition/src/lib/components/component-definitions-preview/model/preview-grid-type.enum';
import { ItemPerRow } from '@rappider/rappider-components/utils';

import {
  projectInformationCardsConfig,
  copiedProjectsListGridConfig,
  copiedProjectsCardConfig,
  addModuleActionButton,
  modalCloseButtonIcon,
  projectModuleTitle,
  projectModuleOptiontitle,
  buildProjectCards,
  formatCopiedProjectsForDisplay
} from './utils';
import { MenuActionBehavior } from 'libs/components/src/lib/utils/menu-two';

@Component({
  selector: 'rappider-project-module',
  templateUrl: './project-module.component.html',
  styleUrls: ['./project-module.component.scss']
})
export class ProjectModuleComponent implements OnInit, OnDestroy {

  @Input() activeProject: Project | undefined;

  publicProjects: Project[] = [];
  publicProjectCategories: Category[] = [];
  copiedProjects: Project[] = [];

  listMode = ListMode.Grid;
  projectInformationModalVisibility = false;
  isModuleOperationsLoading = false;

  addingModuleLoading = false;
  removingModuleLoading = false;
  loadingModuleIds: Set<string> = new Set();

  cardsConfig = projectInformationCardsConfig;
  copiedProjectsCardConfig = copiedProjectsCardConfig;
  listGridConfig = copiedProjectsListGridConfig;

  addModuleAction = addModuleActionButton;
  closeButtonIcon = modalCloseButtonIcon;
  projectModuleTitle = projectModuleTitle;
  projectModuleOptiontitle = projectModuleOptiontitle;

  displayToolbar = false;
  displayToolbarBackButton = false;

  previewModalVisibility = false;
  previewModalTitle = 'Preview';
  previewModalOkText = 'Add Module';
  previewModalCancelText = 'Cancel';
  selectedTemplatePreview: any;
  selectedTemplateNameConfig = {
    typography: {
      fontSize: 'var(--h3-font-size)',
      fontWeight: 'bold'
    },
    colorSettings: {
      color: 'var(--text-color)',
      backgroundColor: 'var(--component-inner-background-color)'
    }
  };
  selectedTemplateDescriptionConfig = {
    typography: {
      fontSize: 'var(--h4-font-size)'
    },
    colorSettings: {
      color: 'var(--text-color)'
    }
  };
  thumbnailTitleConfig = {
    typography: {
      fontSize: 'var(--h5-font-size)',
      fontWeight: 'bold'
    },
    colorSettings: {
      color: 'var(--text-color)'
    }
  };
  thumbnailDescriptionConfig = {
    typography: {
      fontSize: 'var(--h6-font-size)'
    },
    colorSettings: {
      color: 'var(--text-color)'
    }
  };

  private subscriptions: Subscription[] = [];

  constructor(private store: Store) {
  }

  ngOnInit() {
    this.subscribeToData();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToActiveProject(),
      this.subscribeToPublicProjectsWithCategories(),
      this.subscribeToModuleOperationsLoading()
    ];
  }

  /**
   * Subscribe to active project changes to update copied projects list
   */
  subscribeToActiveProject() {
    return this.store.select(selectActiveProject).subscribe((activeProject: Project | undefined) => {
      if (activeProject?.id) {
        this.activeProject = activeProject;
        this.copiedProjects = formatCopiedProjectsForDisplay(
          this.activeProject.copiedProjects,
          this.publicProjectCategories
        );
      }
    });
  }

  /**
   * Subscribe to module operations loading state
   */
  subscribeToModuleOperationsLoading() {
    return this.store.select(selectModuleOperationsLoading).subscribe((loading: boolean) => {
      this.isModuleOperationsLoading = loading;
      if (!loading) {
        this.addingModuleLoading = false;
        this.removingModuleLoading = false;
        this.loadingModuleIds.clear();
      }
    });
  }

  /**
   * Subscribe to public projects and categories combined
   */
  subscribeToPublicProjectsWithCategories() {
    return this.store.select(selectPublicProjectsWithCategories).subscribe((data: any) => {
      if (data) {
        this.publicProjects = data.publicProjects || [];
        this.publicProjectCategories = data.publicProjectCategories || [];

        this.updateCardsConfig();
        this.updateCardsConfigCategories();
      }
    });
  }

  /**
   * Update cards config with public projects
   */
  private updateCardsConfig(): void {
    this.cardsConfig.items = buildProjectCards(this.publicProjects);
  }

  /**
   * Update cards config categories
   */
  private updateCardsConfigCategories(): void {
    if (this.publicProjectCategories?.length) {
      this.cardsConfig.selectConfig.options = this.publicProjectCategories.map(publicProjectCategory => ({
        key: { text: publicProjectCategory.title },
        value: publicProjectCategory.name,
        actionBehavior: MenuActionBehavior.Emit,
        isSelected: false
      })) as any;
    }
  }

  onTopButtonsClick(buttons: any): void {
    if (buttons.key === PreviewGridType.Row) {
      this.cardsConfig.itemCountPerRow = ItemPerRow.One;
      this.cardsConfig.paginationConfig = { pageIndex: 1, total: 2, pageSize: 2, showPagination: true };
    } else if (buttons.key === PreviewGridType.TwoColumns) {
      this.cardsConfig.itemCountPerRow = ItemPerRow.Two;
      this.cardsConfig.paginationConfig = { pageIndex: 1, total: 2, pageSize: 4, showPagination: true };
    } else if (buttons.key === PreviewGridType.FourColumns) {
      this.cardsConfig.itemCountPerRow = ItemPerRow.Four;
      this.cardsConfig.paginationConfig = { pageIndex: 1, total: 2, pageSize: 8, showPagination: true };
    }
  }

  onProjectInformationModalVisibility(visible: boolean): void {
    this.projectInformationModalVisibility = visible;
  }

  onModalConfirm(card: any): void {
    this.addModuleToProject(card.data);
    this.projectInformationModalVisibility = false;
  }

  onAdditionalButtonClick(card: any): void {
    this.addModuleToProject(card.item.data);
    this.projectInformationModalVisibility = false;
  }

  onAddModuleAction(event: any): void {
    this.projectInformationModalVisibility = true;
  }

  onListGridColumnActionClick(actionEvent: any): void {
    this.handleModuleAction(actionEvent);
  }

  onCardListItemActionClicked(actionEvent: any): void {
    this.handleModuleAction(actionEvent);
  }

  /**
   * Handles module actions from both list grid and card views
   * @param actionEvent - The action event containing action details and data
   */
  private handleModuleAction(actionEvent: any): void {
    if (actionEvent?.action?.name === 'delete-module') {
      this.onCopiedProjectDeleted(actionEvent);
    }
  }

  onCopiedProjectDeleted(deletedEvent: any): void {
    if (!this.activeProject?.id || !deletedEvent?.data?.id) {
      return;
    }

    this.loadingModuleIds.add(deletedEvent.data.id);
    this.removingModuleLoading = true;

    this.store.dispatch(new ProjectActions.RemoveCopiedProject({
      copyProjectId: deletedEvent.data.id,
      targetProjectId: this.activeProject.id
    }));
  }

  addModuleToProject(moduleData: any): void {
    if (!this.activeProject?.id) {
      return;
    }

    this.loadingModuleIds.add(moduleData.id);
    this.addingModuleLoading = true;

    this.store.dispatch(new ProjectActions.CopyProject({
      copyProjectId: moduleData.id,
      targetProjectId: this.activeProject.id
    }));
  }

  onImageButtonClick(output) {
    this.selectedTemplatePreview = output.imageButtonData?.item?.data;
    this.previewModalVisibility = true;
  }

  modalOkClick() {
    this.addModuleToProject(this.selectedTemplatePreview);
    this.previewModalVisibility = false;
  }

  modalCancelClick() {
    this.previewModalVisibility = false;
  }

  isModuleLoading(moduleId: string): boolean {
    return this.loadingModuleIds.has(moduleId);
  }
}
